import { useEffect, useRef, useState } from 'react';
import { Button, Caption, List, Placeholder, Title } from '@telegram-apps/telegram-ui';
import {
  BoxPublicView,
  CreateQuestionResponse,
  type ReferralChannel,
} from '@veylme/shared';

import { apiFetch, emitEvent, submitAppeal } from '../api/client.js';
import { ApiAuthError, ApiError } from '../api/errors.js';
import { AgeGateSheet } from '../components/AgeGateSheet.js';
import { QuestionField } from '../components/QuestionField.js';
import { AskSkeleton } from '../components/Skeleton.js';
import { humanizeApiError } from '../lib/apiError.js';
import { useToast } from '../components/ToastProvider.js';
import { acknowledgeAge, isAgeAcknowledged } from '../lib/ageGate.js';
import { clearDraft, readDraft, saveDraft } from '../lib/launchContext.js';
import { addSentQuestion } from '../lib/sentQuestions.js';
import { useT } from '../hooks/useT.js';
import { useMainButton } from '../lib/nativeUi.js';
import { hapticNotification } from '../lib/tg.js';
import styles from './AskScreen.module.css';

const QUESTION_MAX = 500;

/** Пауза перед записью черновика — пишем не на каждую букву (work-038 §5.3). */
const DRAFT_DEBOUNCE_MS = 500;

/**
 * AskScreen — экран отправки анонимного вопроса.
 *
 * Поток:
 *  mount → GET /boxes/:token → BoxPublicView (CANON B2: ни ownerId, ни username,
 *  только token/format/prompt/isActive) → форма → POST /questions →
 *  success state с CTA «Создай свой ящик» → onCreateOwnBox().
 *
 * CANON D1 (важно!): тело POST /questions содержит ТОЛЬКО `boxToken` + `text`.
 * НЕ user_id — сервер его восстанавливает из initData (Authorization header).
 *
 * CANON G3/Q3: 404 / isActive=false → state 'not-found' с сообщением, НЕ падение.
 *
 * Веха 13: главное действие — нативный MainButton (на форме «Отправить вопрос»,
 * на success/not-found «Создай свой ящик»); UI — telegram-ui. CANON I2 age-gate
 * сохранён как СВОЯ нижняя шторка (НЕ Modal кита) — флоу 18+ не трогаем, только
 * цвета на токены. Логика загрузки/отправки/возрастного гейта не изменена.
 *
 * work-036 (Трек D): format-aware копирайт — `box.format === 'message'`
 * («Напиши мне анонимно») переключает заголовок/подсказку/плейсхолдер/success
 * на message_* строки вместо ask_* и tagline; ask-режим (по умолчанию) не тронут.
 *
 * work-038: набранный текст переживает холодный перезапуск приложения (лечение
 * протухшего пропуска Telegram) — черновик пишется в `lib/launchContext.ts` с
 * дебаунсом и подставляется на mount ТОЛЬКО для этого же `token`.
 */
export interface AskScreenProps {
  token: string;
  /** Канал прихода (из start_param) — для атрибуции `ask_opened` (Веха 8 M2). */
  channel: ReferralChannel;
  onCreateOwnBox: () => void;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'loaded'; box: BoxPublicView }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string };

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string }
  // work-024 C: sender_banned — показываем кнопку «Обжаловать»
  | { kind: 'banned'; message: string; appealState: 'idle' | 'sending' | 'sent' };

export function AskScreen({ token, channel, onCreateOwnBox }: AskScreenProps) {
  const t = useT();
  const { showError } = useToast();
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' });
  // work-038: на mount подставляем черновик — но ТОЛЬКО для этого же token
  // (readDraft сам проверяет полное совпадение адресата, см. lib/launchContext.ts).
  const [text, setText] = useState(() => readDraft({ screen: 'ASK', token }) ?? '');
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });
  // CANON I2: age-gate перед первой отправкой. Подтверждение в localStorage —
  // повторно не показываем. Само открытие модалки — user-gesture (onClick submit).
  const [ageGateOpen, setAgeGateOpen] = useState(false);
  // work-038: таймер дебаунса записи черновика — держим в ref, а не в state
  // (переустановка таймера не должна вызывать лишний ререндер).
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // work-038: «черновик потреблён» — выставляется в момент clearDraft() ниже и
  // гасит отложенную запись, если таймер всё же успел встать в очередь до
  // clearTimeout в sendQuestion (защита от гонки, см. докстринг файла).
  const draftConsumedRef = useRef(false);

  // work-038: дебаунс записи черновика — пишем не на каждую букву, а спустя
  // DRAFT_DEBOUNCE_MS после последней правки. Cleanup гасит таймер и при
  // каждой новой правке, и при размонтировании — протечки в следующий тест
  // быть не должно.
  useEffect(() => {
    draftTimerRef.current = setTimeout(() => {
      if (draftConsumedRef.current) return;
      saveDraft({ screen: 'ASK', token, text });
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(draftTimerRef.current);
  }, [token, text]);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/boxes/${encodeURIComponent(token)}`, { parse: BoxPublicView })
      .then((box) => {
        if (cancelled) return;
        if (!box.isActive) {
          setLoadState({ kind: 'not-found' });
          return;
        }
        setLoadState({ kind: 'loaded', box });
        // Веха 8: view-событие `ask_opened` (CANON G2 OK — не share, view-counter).
        // АНОНИМНОЕ (сервер пишет userId=null). fail-soft — emitEvent глотает ошибки.
        void emitEvent({ type: 'ask_opened', boxToken: token, channel });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setLoadState({ kind: 'not-found' });
          return;
        }
        if (err instanceof ApiAuthError) {
          setLoadState({ kind: 'error', message: t.ask_error_restart });
          return;
        }
        setLoadState({ kind: 'error', message: humanizeApiError(err, t) });
      });
    return () => {
      cancelled = true;
    };
  }, [token, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Триггер из MainButton «Отправить»: если 18+ ещё не подтверждено — показываем
  // age-gate вместо отправки (CANON I2). Иначе — сразу шлём.
  function handleSubmit() {
    if (text.trim().length === 0) return;
    if (!isAgeAcknowledged()) {
      setAgeGateOpen(true);
      return;
    }
    void sendQuestion();
  }

  // Подтверждение возраста из модалки (user-gesture) → сохраняем + отправляем.
  function handleAgeConfirm() {
    acknowledgeAge();
    setAgeGateOpen(false);
    void sendQuestion();
  }

  async function sendQuestion() {
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    // work-038 (ревью нашло дефект 1): flush-then-send. Сначала гасим
    // отложенную запись, ЗАТЕМ синхронно пишем черновик на диск и только
    // потом шлём запрос. Раньше здесь стоял голый clearTimeout — если
    // отправка проваливалась (в первую очередь ради 401 всё и затевалось),
    // черновик нигде не оставался: catch-ветка saveDraft не зовёт, а эффект
    // дебаунса не перезапустится (его deps [token, text] не поменялись), и
    // при 401 глобальный перехват мгновенно подменяет экран на
    // SessionExpiredScreen — композер размонтируется, набранный текст исчезал
    // бесследно ровно в момент, ради которого весь work-038 и затевался.
    // Запись ДО сетевого вызова это чинит и НЕ возвращает старый дефект
    // «отправленный текст воскресает»: отложенного таймера уже нет (clearTimeout
    // выше), а при успехе ниже clearDraft стирает запись — инвариант «после
    // успешной отправки в хранилище черновика нет» держится.
    clearTimeout(draftTimerRef.current);
    saveDraft({ screen: 'ASK', token, text });
    setSubmitState({ kind: 'submitting' });
    try {
      // CANON D1: НЕ слать user_id — только boxToken + text.
      const created = await apiFetch('/questions', {
        method: 'POST',
        body: { boxToken: token, text: trimmed },
        parse: CreateQuestionResponse,
      });
      // retention-answer-loop: запоминаем questionId на устройстве спрашивающего
      // (localStorage, БЕЗ серверной identity) — «Мои вопросы» подтянет ответ
      // по этому id, когда автор ответит.
      const promptForLabel =
        loadState.kind === 'loaded' ? loadState.box.prompt : null;
      addSentQuestion({
        questionId: created.questionId,
        createdAt: new Date().toISOString(),
        label: promptForLabel ?? undefined,
      });
      // work-038: черновик потреблён — стираем и гасим отложенную запись.
      draftConsumedRef.current = true;
      clearDraft({ screen: 'ASK', token });
      hapticNotification('success');
      setSubmitState({ kind: 'success' });
    } catch (err: unknown) {
      // W1: единый словарь ошибок — тост + человекочитаемая красная рамка под
      // полем (никакого сырого err.message в UI). Образец — ShareScreen.
      const message = humanizeApiError(err, t);
      hapticNotification('error');
      // work-024 C: sender_banned → специальное состояние с кнопкой «Обжаловать»
      if (err instanceof ApiError && err.code === 'sender_banned') {
        setSubmitState({ kind: 'banned', message, appealState: 'idle' });
      } else {
        showError(message);
        setSubmitState({ kind: 'error', message });
      }
    }
  }

  // work-024 C: отправка апелляции при бане
  async function handleAppeal() {
    if (submitState.kind !== 'banned') return;
    setSubmitState({ ...submitState, appealState: 'sending' });
    try {
      await submitAppeal();
      setSubmitState({ ...submitState, appealState: 'sent' });
    } catch {
      // fail-soft: не ломаем UI, сброс в idle чтобы можно было повторить
      setSubmitState({ ...submitState, appealState: 'idle' });
    }
  }

  const submitting = submitState.kind === 'submitting';
  const canSubmit = !submitting && text.trim().length > 0;
  const isLoadedForm =
    loadState.kind === 'loaded' &&
    submitState.kind !== 'success' &&
    submitState.kind !== 'banned';
  const showCreateOwn =
    submitState.kind === 'success' || loadState.kind === 'not-found';
  // work-036 (Трек D): format-aware копирайт — 'message' ящик («Напиши мне
  // анонимно») использует свой набор строк вместо ask_*/tagline. box.format
  // известен, только когда loadState.kind === 'loaded' (иначе false — не
  // критично, not-found/error не используют эти строки).
  const isMessage = loadState.kind === 'loaded' && loadState.box.format === 'message';

  // Единый MainButton (Веха 13.2): на форме — «Отправить вопрос» (свежий text
  // через ref хука), на success/not-found — «Создай свой ящик». Под открытой
  // age-шторкой кнопку прячем — там свои действия.
  useMainButton(
    ageGateOpen
      ? { text: '', onClick: () => undefined, visible: false }
      : isLoadedForm
        ? {
            text: submitting ? t.ask_sending : t.ask_send,
            onClick: handleSubmit,
            enabled: canSubmit,
            loading: submitting,
          }
        : showCreateOwn
          ? { text: t.ask_create_own_box, onClick: onCreateOwnBox }
          : { text: '', onClick: () => undefined, visible: false },
  );

  function renderBody() {
    if (loadState.kind === 'loading') {
      return <AskSkeleton />;
    }
    if (loadState.kind === 'not-found') {
      return (
        <Placeholder
          header={t.ask_box_unavailable}
          description={t.ask_box_unavailable_desc}
        />
      );
    }
    if (loadState.kind === 'error') {
      return (
        <Placeholder header={t.error_title} description={loadState.message} />
      );
    }
    if (submitState.kind === 'success') {
      return (
        <Placeholder
          header={isMessage ? t.message_sent_title : t.ask_sent_title}
          description={
            isMessage ? (
              t.message_sent_desc
            ) : (
              <>
                {t.ask_sent_desc}
                <br />
                <Caption level="1" className={styles.hint}>
                  {t.ask_sent_find_reply_hint}
                </Caption>
              </>
            )
          }
        />
      );
    }
    // work-024 C: бан — показываем ошибку + кнопку «Обжаловать»
    if (submitState.kind === 'banned') {
      return (
        <Placeholder
          header={t.error_title}
          description={
            submitState.appealState === 'sent'
              ? t.ask_appeal_sent
              : submitState.message
          }
          action={
            submitState.appealState !== 'sent' ? (
              <Button
                size="m"
                onClick={() => void handleAppeal()}
                disabled={submitState.appealState === 'sending'}
              >
                {t.ask_banned_appeal_btn}
              </Button>
            ) : undefined
          }
        />
      );
    }

    // loaded form
    const promptText =
      loadState.box.prompt ?? (isMessage ? t.message_tagline : t.tagline);
    return (
      <>
        <div className={styles.head}>
          <Title level="2" weight="2">
            {promptText}
          </Title>
          <Caption level="1" className={styles.hint}>
            {isMessage ? t.message_anonymous_hint : t.ask_anonymous_hint}
          </Caption>
        </div>
        <QuestionField
          value={text}
          onChange={setText}
          maxLength={QUESTION_MAX}
          placeholder={isMessage ? t.message_placeholder : t.ask_placeholder}
          disabled={submitting}
          error={
            submitState.kind === 'error' ? submitState.message : undefined
          }
        />
      </>
    );
  }

  return (
    <List>
      {renderBody()}

      <AgeGateSheet
        open={ageGateOpen}
        onConfirm={handleAgeConfirm}
        onCancel={() => setAgeGateOpen(false)}
      />
    </List>
  );
}
